import { FC, useCallback } from 'react';

import { RyeconColorDbt, RyeconColorSnowflake } from '@dbt-labs/sourdough';

import { DataPlatform, SearchPlatform } from '@/__generated__/graphql';
import { useFilterContext } from '@/context/FilterProvider';
import { useSearchFacets } from '@/context/SearchFacetsProvider';
import { useAvailableDataPlatforms } from '@/hooks/discovery/catalog/availablePlatforms';
import { BaseFilterToggle } from './BaseFilterToggle';
import { ClearSelectAllButton } from './ClearSelectAllButton';
import { FilterSection } from './FilterSection';

export type PlatformFiltersProps = {};

const SELECTION_LIST = [
  { label: 'dbt', value: SearchPlatform.Dbt, ryecon: RyeconColorDbt },
];

const ALL_DATA_PLATFORMS_SELECTION_LIST = [
  { label: 'Snowflake', value: SearchPlatform.Snowflake, ryecon: RyeconColorSnowflake },
];

export const PlatformFilters: FC<PlatformFiltersProps> = () => {
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();

  const availablePlatformsQuery = useAvailableDataPlatforms({});

  const platforms = filterCtx.filters?.platforms;

  const handleToggle = useCallback(
    (field: SearchPlatform) => {
      const isAdding = !platforms?.includes(field);
      filterCtx.updateFilters({ platforms: [field] }, isAdding);
    },
    [filterCtx, platforms],
  );

  const availablePlatformFilters = SELECTION_LIST.concat(
    ALL_DATA_PLATFORMS_SELECTION_LIST.filter((selection) =>
      availablePlatformsQuery.data?.includes(
        DataPlatform[selection.label as keyof typeof DataPlatform],
      ),
    ),
  );

  return (
    <FilterSection title="Platform">
      <ul className="mt-2">
        {(availablePlatformsQuery.fetching
          ? SELECTION_LIST
          : availablePlatformFilters
        ).map((selection) => (
          <li key={selection.value}>
            <BaseFilterToggle
              ryecon={selection.ryecon}
              isChecked={platforms?.includes(selection.value)}
              onToggle={() => {
                handleToggle(selection.value);
              }}
              count={aggregations?.platform?.[selection.value]}
            >
              {selection.label}
            </BaseFilterToggle>
          </li>
        ))}
      </ul>
      <ClearSelectAllButton
        type="platforms"
        selectedList={platforms}
        selectionList={SELECTION_LIST}
      />
    </FilterSection>
  );
};
